import type {StoreApi} from 'zustand/vanilla';

import type {CellsV5} from '../../types/Api';
import type {RowData} from '../../types/RowData';
import type {EagGridState, EagGridStore, FetchState} from '../createEagGridStore';

type ServerDataActions = Pick<
  EagGridStore,
  | 'setRows'
  | 'setTotalCount'
  | 'setSummaryRowCells'
  | 'startFetch'
  | 'settleFetch'
  | 'mergeRowCells'
  | 'setQueryId'
  | 'addPendingColumns'
  | 'removePendingColumns'
>;

/** Server-data actions. Bodies moved verbatim from createEagGridStore.ts. */
export function createServerDataActions(
  store: StoreApi<EagGridState>,
  clampFocusedRow: EagGridStore['clampFocusedRow']
): ServerDataActions {
  return {
    setRows: (rows, loadedPageRequest) => {
      store.setState({
        rows,
        loadedPageRequest: loadedPageRequest ?? null,
        rowSelectionAnchorId: null,
      });
      // Keep keyboard focus valid when the row set changes (filter/refetch).
      // clampFocusedRow no-ops when there is no keyboard focus or the index is still in range.
      clampFocusedRow(rows.length);
    },

    setTotalCount: (totalCount: number, queryId = store.getState().queryId) => {
      const state = store.getState();
      if (state.totalCount === totalCount && state.totalCountQueryId === queryId) {
        return;
      }
      store.setState({totalCount, totalCountQueryId: queryId});
    },

    startFetch: () => {
      store.setState({
        fetchState: 'loading',
        previousRows: store.getState().rows,
        selectedCellRange: null,
        rowSelectionAnchorId: null,
      });
    },

    settleFetch: (target: Exclude<FetchState, 'loading'>) => {
      store.setState({fetchState: target, previousRows: []});
    },

    mergeRowCells: (patchByRowId: Map<string, Partial<RowData>>) => {
      const state = store.getState();
      let changed = false;
      const rows = state.rows.map((row) => {
        const patch = patchByRowId.get(row.id);
        if (!patch) {
          return row;
        }
        changed = true;
        return {...row, ...patch};
      });
      if (changed) {
        store.setState({rows});
      }
    },

    setQueryId: (queryId: string) => {
      store.setState({
        queryId,
        bulkSelectionQueryId:
          queryId === store.getState().queryId ? store.getState().bulkSelectionQueryId : null,
        rowSelectionAnchorId:
          queryId === store.getState().queryId ? store.getState().rowSelectionAnchorId : null,
      });
    },

    setSummaryRowCells: (cells: Record<string, CellsV5> | null) => {
      store.setState({summaryRowCells: cells});
    },

    // Pending flags are REF-COUNTED: overlapping delta fetches for the same column
    // (show → hide → show before the first settles) each add/remove one count, so
    // the first fetch to settle cannot strip a skeleton a later fetch still owns.
    // pendingColumnKeys stays the flat derived list consumed by the UI.
    addPendingColumns: (keys: string[]) => {
      if (keys.length === 0) {
        return;
      }
      const state = store.getState();
      const nextCounts = {...state.pendingColumnCounts};
      const newKeys: string[] = [];
      for (const key of keys) {
        const count = nextCounts[key] ?? 0;
        if (count === 0) {
          newKeys.push(key);
        }
        nextCounts[key] = count + 1;
      }
      const nextKeys =
        newKeys.length > 0 ? [...state.pendingColumnKeys, ...newKeys] : state.pendingColumnKeys;
      store.setState({pendingColumnCounts: nextCounts, pendingColumnKeys: nextKeys});
    },

    removePendingColumns: (keys: string[]) => {
      const state = store.getState();
      const nextCounts = {...state.pendingColumnCounts};
      const cleared = new Set<string>();
      let changed = false;
      for (const key of keys) {
        const count = nextCounts[key];
        if (count === undefined) {
          continue; // never added (or already fully cleared) — nothing to balance
        }
        changed = true;
        if (count > 1) {
          nextCounts[key] = count - 1;
        } else {
          delete nextCounts[key];
          cleared.add(key);
        }
      }
      if (!changed) {
        return;
      }
      const nextKeys =
        cleared.size > 0
          ? state.pendingColumnKeys.filter((key) => !cleared.has(key))
          : state.pendingColumnKeys;
      store.setState({pendingColumnCounts: nextCounts, pendingColumnKeys: nextKeys});
    },
  };
}
